import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut as firebaseSignOut, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, collection, collectionGroup, getDocs, doc, getDoc, updateDoc, deleteDoc, writeBatch, addDoc, query, orderBy, where, onSnapshot, setDoc, limit } from 'firebase/firestore';
import { getStorage, ref, listAll, deleteObject } from 'firebase/storage';
import { User } from '../types';
import type { Message, ContactConversation, MessageStatus } from '../types';
import { notifyUser } from './pushApi';

export interface LastActivityInfo {
  date: string;
  type: string;
}

export interface UserFinancialSummary {
  payableCount: number;
  receivableCount: number;
}

const firebaseConfig = {
  apiKey: "AIzaSyCJOVmGt685slcLfcjiq1jujxOViIPh5hE",
  authDomain: "meu-negocio-f116b.firebaseapp.com",
  projectId: "meu-negocio-f116b",
  storageBucket: "meu-negocio-f116b.firebasestorage.app",
  messagingSenderId: "173247310602",
  appId: "1:173247310602:web:be5cc0c622fe10ca9b68f3",
  measurementId: "G-2EQHHWH8NC"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

/**
 * Gera timestamp no horário local (sem conversão para UTC)
 */
const getBrazilTimestamp = (): string => {
  const now = new Date();
  // Compensar o timezone offset para salvar o horário local como se fosse UTC
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString();
};

export const signInWithEmail = async (email: string, password: string) => {
  return await signInWithEmailAndPassword(auth, email, password);
};

export const signOut = async () => {
  return await firebaseSignOut(auth);
};

export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    throw error;
  }
};

export const checkUserRole = async (uid: string): Promise<'admin' | 'user' | null> => {
  try {
    const docRef = doc(db, 'user', uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const role = data.role || 'user';
      return role;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

export const fetchUsers = async (): Promise<User[]> => {
  const usersCollection = collection(db, 'user');
  const userSnapshot = await getDocs(usersCollection);
  
  const usersPromises = userSnapshot.docs.map(async (doc: any) => {
    const data = doc.data();
    const docId = doc.id;
    
    const salesCount = await fetchUserSales(docId).then(sales => sales.length);
    
    return {
      uid: docId,
      id: data.id,
      salesCount,
      ...data,
    } as User;
  });
  
  const users = await Promise.all(usersPromises);
  
  return users;
};

export const fetchUserById = async (id: string) => {
  try {
    const docRef = doc(db, 'user', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return { 
        uid: docSnap.id,
        id: data.id,
        ...data 
      };
    }
    
    throw new Error('User not found');
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (uid: string, data: Partial<User>) => {
  try {
    const docRef = doc(db, 'user', uid);
    await updateDoc(docRef, data);
  } catch (error) {
    throw error;
  }
};

export const fetchUserProducts = async (uid: string) => {
  try {
    const productsCollection = collection(db, `product-${uid}`);
    const productsSnapshot = await getDocs(productsCollection);
    
    const products = productsSnapshot.docs.map((docSnap: any) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
    
    return products;
  } catch (error) {
    return [];
  }
};

export const fetchUserServices = async (uid: string) => {
  try {
    const servicesCollection = collection(db, `service-${uid}`);
    const servicesSnapshot = await getDocs(servicesCollection);

    const services = servicesSnapshot.docs
      .map((docSnap: any) => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      .sort((a: any, b: any) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));

    return services;
  } catch (error) {
    return [];
  }
};

export const fetchUserSales = async (uid: string) => {
  try {
    const salesCollection = collection(db, `sale-${uid}`);
    const salesSnapshot = await getDocs(salesCollection);
    
    const sales = salesSnapshot.docs.map((docSnap: any) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
    
    return sales;
  } catch (error) {
    return [];
  }
};

export const deleteUserSale = async (uid: string, saleId: string, financeIds: string[] = []): Promise<void> => {
  try {
    const uniqueFinanceIds = Array.from(new Set(financeIds.filter(Boolean)));

    if (uniqueFinanceIds.length > 0) {
      const batch = writeBatch(db);

      uniqueFinanceIds.forEach((financeId) => {
        batch.delete(doc(db, `finance-${uid}`, financeId));
      });

      await batch.commit();
    }

    await deleteDoc(doc(db, `sale-${uid}`, saleId));

    console.log('✅ Venda excluída:', saleId);
  } catch (error) {
    console.error('❌ Erro ao excluir venda:', error);
    throw error;
  }
};

export const fetchUserServiceOrders = async (uid: string) => {
  try {
    const serviceOrdersCollection = collection(db, `serviceOrder-${uid}`);
    const serviceOrdersSnapshot = await getDocs(serviceOrdersCollection);

    const serviceOrders = serviceOrdersSnapshot.docs.map((docSnap: any) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    return serviceOrders;
  } catch (error) {
    return [];
  }
};

export const fetchUserCategories = async (uid: string) => {
  try {
    const categoriesCollection = collection(db, `category-${uid}`);
    const categoriesSnapshot = await getDocs(categoriesCollection);

    const categories = categoriesSnapshot.docs.map((docSnap: any) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    return categories;
  } catch (error) {
    return [];
  }
};

export const fetchUserCostumers = async (uid: string) => {
  try {
    const costumersCollection = collection(db, `costumer-${uid}`);
    const costumersSnapshot = await getDocs(costumersCollection);

    const costumers = costumersSnapshot.docs.map((docSnap: any) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    return costumers;
  } catch (error) {
    return [];
  }
};

export const fetchUserPayMethods = async (uid: string) => {
  try {
    const payMethodsCollection = collection(db, `payMethod-${uid}`);
    const payMethodsSnapshot = await getDocs(payMethodsCollection);

    const payMethods = payMethodsSnapshot.docs.map((docSnap: any) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    return payMethods;
  } catch (error) {
    return [];
  }
};

const countCollectionDocs = async (collectionName: string): Promise<number> => {
  try {
    const snap = await getDocs(collection(db, collectionName));
    return snap.size;
  } catch {
    return 0;
  }
};

const classifyFinanceType = (data: Record<string, any>): 'payable' | 'receivable' | null => {
  const typeCandidate = [
    data.type,
    data.kind,
    data.category,
    data.accountType,
    data.flow,
    data.movementType,
    data.transactionType,
  ]
    .filter(Boolean)
    .join(' ')
    .toString()
    .toLowerCase();

  const receivablePattern = /(receber|receivable|income|entrada|inflow|credito|credit|venda)/;
  const payablePattern = /(pagar|payable|expense|despesa|saida|outflow|debito|debit|compra)/;

  if (receivablePattern.test(typeCandidate)) return 'receivable';
  if (payablePattern.test(typeCandidate)) return 'payable';

  const valueCandidate =
    typeof data.value === 'number'
      ? data.value
      : typeof data.amount === 'number'
        ? data.amount
        : typeof data.total === 'number'
          ? data.total
          : null;

  if (typeof valueCandidate === 'number') {
    if (valueCandidate > 0) return 'receivable';
    if (valueCandidate < 0) return 'payable';
  }

  return null;
};

export const fetchUserFinancialSummary = async (uid: string): Promise<UserFinancialSummary> => {
  const summary: UserFinancialSummary = {
    payableCount: 0,
    receivableCount: 0,
  };

  try {
    const financeCollection = collection(db, `finance-${uid}`);
    const financeSnapshot = await getDocs(financeCollection);

    if (!financeSnapshot.empty) {
      financeSnapshot.docs.forEach((docSnap) => {
        const classification = classifyFinanceType(docSnap.data());
        if (classification === 'payable') summary.payableCount += 1;
        if (classification === 'receivable') summary.receivableCount += 1;
      });

      return summary;
    }
  } catch {
    // continua para fallback por aliases de coleção
  }

  const payablePrefixes = [
    'accountPayable',
    'accountsPayable',
    'payable',
    'toPay',
    'financePayable',
    'financeToPay',
    'billToPay',
    'contaPagar',
    'contasPagar',
  ];

  const receivablePrefixes = [
    'accountReceivable',
    'accountsReceivable',
    'receivable',
    'toReceive',
    'financeReceivable',
    'financeToReceive',
    'billToReceive',
    'contaReceber',
    'contasReceber',
  ];

  const [payableCounts, receivableCounts] = await Promise.all([
    Promise.all(payablePrefixes.map((prefix) => countCollectionDocs(`${prefix}-${uid}`))),
    Promise.all(receivablePrefixes.map((prefix) => countCollectionDocs(`${prefix}-${uid}`))),
  ]);

  summary.payableCount = payableCounts.reduce((acc, n) => acc + n, 0);
  summary.receivableCount = receivableCounts.reduce((acc, n) => acc + n, 0);

  return summary;
};

/**
 * Cardápio/Catálogo digital do lojista. `slug` monta a URL pública e `active`
 * reflete o campo `ativo` de `digitalMenus`.
 */
export interface DigitalMenuInfo {
  slug: string;
  active: boolean;
}

/**
 * Mapa businessId -> cardápio digital.
 *
 * Varre as duas coleções globais (`digitalMenus`, chaveada pelo uid do lojista,
 * e o índice reverso `menuSlugs`, chaveado pelo slug). Custa uma leitura por
 * cardápio existente — e não uma por cliente do painel, como seria consultar
 * cada lojista individualmente.
 */
export const fetchDigitalMenusByBusiness = async (): Promise<Record<string, DigitalMenuInfo>> => {
  const menus: Record<string, DigitalMenuInfo> = {};

  try {
    const menusSnapshot = await getDocs(collection(db, 'digitalMenus'));
    menusSnapshot.docs.forEach((docSnap) => {
      const data = docSnap.data() as { businessId?: string; slug?: string; ativo?: boolean };
      // Documentos legados são chaveados pelo slug; os atuais, pelo uid do lojista.
      const businessId = data.businessId || docSnap.id;
      if (!businessId) return;

      menus[businessId] = {
        slug: data.slug || (data.businessId ? docSnap.id : ''),
        active: data.ativo === true,
      };
    });
  } catch (error) {
    console.error('Erro ao carregar cardápios digitais:', error);
  }

  try {
    const slugsSnapshot = await getDocs(collection(db, 'menuSlugs'));
    slugsSnapshot.docs.forEach((docSnap) => {
      const businessId = (docSnap.data() as { businessId?: string }).businessId;
      if (!businessId) return;

      const existing = menus[businessId];
      // O índice só completa o que faltou: `digitalMenus` é a fonte de `ativo`
      // e do slug corrente.
      menus[businessId] = {
        slug: existing?.slug || docSnap.id,
        active: existing?.active ?? false,
      };
    });
  } catch (error) {
    console.error('Erro ao carregar slugs de cardápio:', error);
  }

  return menus;
};

/**
 * Indica se o lojista usa de fato o módulo Financeiro, e não apenas registra
 * vendas: procura um lançamento em `finance-{uid}` que não tenha sido gerado
 * automaticamente por venda/OS.
 *
 * Venda e OS gravam o lançamento com `origin` "mobile" (app) ou "web" (ERP) e
 * sempre `type: credit`. Despesas e receitas avulsas — únicas que podem ter
 * `type: debit` — são gravadas com `origin: manual`. Daí as duas consultas:
 * a primeira pega qualquer lançamento manual, a segunda cobre despesas antigas
 * gravadas antes do campo `origin` existir.
 *
 * Custa 1 leitura por cliente (2 quando não há lançamento manual), então quem
 * chama deve cachear o resultado.
 */
export const fetchUsesFinanceModule = async (uid: string): Promise<boolean> => {
  const financeCollection = collection(db, `finance-${uid}`);

  try {
    const manualSnapshot = await getDocs(
      query(financeCollection, where('origin', '==', 'manual'), limit(1))
    );
    if (!manualSnapshot.empty) return true;

    const debitSnapshot = await getDocs(
      query(financeCollection, where('type', '==', 'debit'), limit(1))
    );
    return !debitSnapshot.empty;
  } catch (error) {
    console.error(`Erro ao verificar uso do financeiro de ${uid}:`, error);
    return false;
  }
};

/**
 * Busca a data da última atividade do usuário em todas as suas coleções.
 * Tenta ordenar por campos de data comuns; retorna o registro mais recente.
 */
export const fetchLastActivityInfo = async (userUid: string): Promise<LastActivityInfo | null> => {
  const collectionPrefixes: Array<{ prefix: string; type: string }> = [
    { prefix: 'product', type: 'Produto cadastrado' },
    { prefix: 'service', type: 'Serviço cadastrado' },
    { prefix: 'serviceOrder', type: 'OS criada/atualizada' },
    { prefix: 'category', type: 'Categoria cadastrada' },
    { prefix: 'costumer', type: 'Cliente cadastrado' },
    { prefix: 'goal', type: 'Meta atualizada' },
    { prefix: 'payMethod', type: 'Conta/forma no financeiro' },
    { prefix: 'sale', type: 'Venda realizada' },
  ];
  const dateFields = ['finishedAt', 'canceledAt', 'updatedAt', 'createdAt', 'date'];

  let mostRecentDate: Date | null = null;
  let mostRecentType: string | null = null;

  await Promise.all(
    collectionPrefixes.map(async ({ prefix, type }) => {
      const collName = `${prefix}-${userUid}`;
      for (const field of dateFields) {
        try {
          const q = query(collection(db, collName), orderBy(field, 'desc'), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const val = snap.docs[0].data()[field];
            if (val) {
              const d = val.toDate ? val.toDate() : new Date(val);
              if (!isNaN(d.getTime())) {
                if (!mostRecentDate || d > mostRecentDate) {
                  mostRecentDate = d;
                  mostRecentType = type;
                }
              }
            }
            break; // campo encontrado para esta coleção
          }
        } catch {
          // campo não existe nesta coleção, tenta o próximo
        }
      }
    })
  );

  if (!mostRecentDate) {
    return null;
  }

  return {
    date: new Date(mostRecentDate).toISOString(),
    type: mostRecentType || 'Atividade registrada',
  };
};

export const fetchLastActivity = async (userUid: string): Promise<string | null> => {
  const info = await fetchLastActivityInfo(userUid);
  return info?.date || null;
};

/**
 * Função para excluir uma coleção completa
 */
const deleteCollection = async (collectionPath: string) => {
  try {
    const collectionRef = collection(db, collectionPath);
    const snapshot = await getDocs(collectionRef);
    
    // Usar batch para deletar em lotes de 500 (limite do Firestore)
    const batchSize = 500;
    const batches: any[] = [];
    let batch = writeBatch(db);
    let batchCount = 0;
    
    snapshot.docs.forEach((document) => {
      batch.delete(document.ref);
      batchCount++;
      
      if (batchCount === batchSize) {
        batches.push(batch.commit());
        batch = writeBatch(db);
        batchCount = 0;
      }
    });
    
    // Commit do último batch se houver documentos
    if (batchCount > 0) {
      batches.push(batch.commit());
    }
    
    await Promise.all(batches);
    console.log(`Coleção ${collectionPath} excluída com sucesso`);
  } catch (error) {
    console.error(`Erro ao excluir coleção ${collectionPath}:`, error);
    throw error;
  }
};

/**
 * Função para excluir todos os arquivos de uma pasta no Storage
 */
const deleteStorageFolder = async (folderPath: string) => {
  try {
    const folderRef = ref(storage, folderPath);
    const listResult = await listAll(folderRef);
    
    // Deletar todos os arquivos
    const deletePromises = listResult.items.map((itemRef) => deleteObject(itemRef));
    await Promise.all(deletePromises);
    
    // Deletar subpastas recursivamente
    const folderPromises = listResult.prefixes.map((folderRef) => 
      deleteStorageFolder(folderRef.fullPath)
    );
    await Promise.all(folderPromises);
    
    console.log(`Pasta ${folderPath} excluída com sucesso`);
  } catch (error) {
    console.error(`Erro ao excluir pasta ${folderPath}:`, error);
    // Não lançar erro se a pasta não existir
    if ((error as any).code !== 'storage/object-not-found') {
      throw error;
    }
  }
};

/**
 * Função para excluir um arquivo específico no Storage
 */
const deleteStorageFile = async (filePath: string) => {
  try {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
    console.log(`Arquivo ${filePath} excluído com sucesso`);
  } catch (error) {
    console.error(`Erro ao excluir arquivo ${filePath}:`, error);
    // Não lançar erro se o arquivo não existir
    if ((error as any).code !== 'storage/object-not-found') {
      throw error;
    }
  }
};

/**
 * Função para admin excluir completamente um cliente (sem necessidade de senha)
 * 
 * EXCLUSÕES REALIZADAS:
 * 1. ✅ Todas as imagens do Storage (business e produtos)
 * 2. ✅ Todas as coleções do Firestore (incluindo vendas, serviços e OS)
 * 3. ✅ Documento do usuário em 'user' collection
 * 
 * NOTA IMPORTANTE:
 * ❌ A conta do Firebase Authentication NÃO será excluída.
 * - Apenas o próprio usuário (logado com senha) pode excluir sua conta Auth
 * - A conta Auth ficará "órfã" (sem dados no Firestore/Storage)
 * - Isso é normal para limpeza administrativa
 * - O usuário não conseguirá mais usar a conta pois não há dados associados
 */
export const deleteUserCompletely = async (uid: string): Promise<void> => {
  try {
    console.log(`🔥 Iniciando exclusão administrativa do usuário ${uid}...`);
    console.log(`👤 Admin permanece logado durante todo o processo.`);
    
    // PASSO 1: Excluir todas as imagens do Storage
    console.log('\n📦 PASSO 1: Excluindo imagens do Storage...');
    
    // 1.1. Excluir imagem do negócio
    try {
      const businessImagePath = `images/business/img-${uid}.png`;
      console.log(`  🖼️  Tentando excluir: ${businessImagePath}...`);
      await deleteStorageFile(businessImagePath);
      console.log(`  ✅ Excluído: ${businessImagePath}`);
    } catch (storageError) {
      console.log(`  ⏭️  Imagem do negócio não encontrada (normal se não houver)`);
    }
    
    // 1.2. Excluir pasta de produtos
    try {
      const productPath = `images/product-${uid}`;
      console.log(`  📁 Excluindo pasta de produtos: ${productPath}...`);
      await deleteStorageFolder(productPath);
      console.log(`  ✅ Pasta excluída: ${productPath}`);
    } catch (storageError) {
      console.log(`  ⏭️  Pasta de produtos não encontrada (normal se não houver)`);
    }
    
    // PASSO 2: Excluir todas as coleções do Firestore
    console.log('\n📚 PASSO 2: Excluindo coleções do Firestore...');
    const collections = [
      `costumer-${uid}`,
      `deletedCustomer-${uid}`,
      `product-${uid}`,
      `deletedProduct-${uid}`,
      `service-${uid}`,
      `deletedService-${uid}`,
      `payMethod-${uid}`,
      `sale-${uid}`,
      `serviceOrder-${uid}`
    ];
    
    for (const collectionName of collections) {
      try {
        console.log(`  📂 Excluindo coleção: ${collectionName}...`);
        await deleteCollection(collectionName);
        console.log(`  ✅ Coleção excluída: ${collectionName}`);
      } catch (collectionError) {
        console.log(`  ⏭️  Coleção ${collectionName} vazia ou não existe`);
      }
    }
    
    // PASSO 3: Excluir documento do usuário
    console.log('\n👤 PASSO 3: Excluindo documento do usuário...');
    try {
      const userDocRef = doc(db, 'user', uid);
      await deleteDoc(userDocRef);
      console.log(`  ✅ Documento excluído: user/${uid}`);
    } catch (docError) {
      console.error(`  ❌ Erro crítico ao excluir documento do usuário:`, docError);
      throw docError;
    }
    
    console.log(`\n✅ Exclusão administrativa concluída com sucesso!`);
    console.log(`� Resumo:`);
    console.log(`  ✅ Storage limpo (imagens deletadas)`);
    console.log(`  ✅ Firestore limpo (coleções principais + documento user)`);
    console.log(`  ⚠️  Authentication: conta órfã (sem dados, mas conta existe)`);
    console.log(`  👤 Admin permanece logado normalmente`);
    
  } catch (error) {
    console.error('\n❌ Erro ao excluir usuário:', error);
    throw error;
  }
};

// ==========================================
// CRUD de Planos (Plan)
// ==========================================

interface Plan {
  id: string;
  days: number;
  device: number;
  value: number;
}

interface PlanFormData {
  days: number;
  device: number;
  value: number;
}

/**
 * Buscar todos os planos
 */
export const fetchPlans = async (): Promise<Plan[]> => {
  try {
    const plansRef = collection(db, 'plan');
    const snapshot = await getDocs(plansRef);
    
    const plans: Plan[] = [];
    snapshot.forEach((doc) => {
      plans.push({
        id: doc.id,
        ...doc.data()
      } as Plan);
    });
    
    console.log('✅ Planos carregados:', plans.length);
    return plans;
  } catch (error) {
    console.error('❌ Erro ao buscar planos:', error);
    throw error;
  }
};

/**
 * Criar novo plano
 */
export const createPlan = async (data: PlanFormData): Promise<string> => {
  try {
    const plansRef = collection(db, 'plan');
    const docRef = await addDoc(plansRef, {
      days: data.days,
      device: data.device,
      value: data.value,
    });
    
    console.log('✅ Plano criado:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Erro ao criar plano:', error);
    throw error;
  }
};

/**
 * Atualizar plano existente
 */
export const updatePlan = async (planId: string, data: PlanFormData): Promise<void> => {
  try {
    const planRef = doc(db, 'plan', planId);
    await updateDoc(planRef, {
      days: data.days,
      device: data.device,
      value: data.value,
    });
    
    console.log('✅ Plano atualizado:', planId);
  } catch (error) {
    console.error('❌ Erro ao atualizar plano:', error);
    throw error;
  }
};

/**
 * Excluir plano
 */
export const deletePlan = async (planId: string): Promise<void> => {
  try {
    const planRef = doc(db, 'plan', planId);
    await deleteDoc(planRef);
    
    console.log('✅ Plano excluído:', planId);
  } catch (error) {
    console.error('❌ Erro ao excluir plano:', error);
    throw error;
  }
};

// =============================================================================
// FUNÇÕES DE SUPORTE/CHAT
// =============================================================================

/**
 * Buscar todas as conversas de suporte (lista de usuários que já enviaram mensagens)
 */
export const fetchContactConversations = async (): Promise<ContactConversation[]> => {
  try {
    console.log('🔍 Buscando conversas de suporte...');
    console.log('👤 Admin autenticado:', auth.currentUser?.email);
    
    // NOVA ESTRATÉGIA: Buscar todos os usuários e verificar se têm mensagens
    // Isso funciona porque os documentos vazios em contactMessages não aparecem,
    // mas podemos buscar as subcoleções diretamente através dos IDs dos usuários
    
    const usersRef = collection(db, 'user');
    const usersSnapshot = await getDocs(usersRef);
    console.log(`📊 Total de usuários no sistema: ${usersSnapshot.docs.length}`);
    
    const conversationsPromises = usersSnapshot.docs.map(async (userDoc) => {
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      // Buscar mensagens deste usuário em contactMessages/{userId}/messages
      const messagesRef = collection(db, 'contactMessages', userId, 'messages');
      
      let messagesSnapshot;
      try {
        const messagesQuery = query(messagesRef, orderBy('createdAt', 'desc'), limit(1));
        messagesSnapshot = await getDocs(messagesQuery);
      } catch (orderByError) {
        // Se orderBy falhar (falta de índice), buscar sem ordenação
        messagesSnapshot = await getDocs(messagesRef);
      }
      
      // Se não tem mensagens, não incluir na lista
      if (messagesSnapshot.empty) {
        return null;
      }
      
      console.log(`👤 ${userData.businessName}: ${messagesSnapshot.docs.length} mensagens`);
      
      // Pegar todas as mensagens para ordenar e contar não lidas
      const allMessagesQuery = query(messagesRef);
      const allMessagesSnapshot = await getDocs(allMessagesQuery);
      
      const allMessages = allMessagesSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            msg: data.msg || '',
            fromMe: data.fromMe || false,
            createdAt: data.createdAt || '',
            status: data.status || 'sent',
            ...data
          };
        })
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        });
      
      const lastMsg = allMessages[0];
      const lastMessage = lastMsg.msg || '';
      const lastMessageDate = lastMsg.createdAt || '';
      
      // Contar mensagens não lidas (do cliente para o admin)
      const unreadCount = allMessages.filter(msg => 
        msg.fromMe === true && msg.status === 'sent'
      ).length;
      
      return {
        userId,
        businessName: userData.businessName || 'Usuário Desconhecido',
        mail: userData.mail || '',
        photoUrl: userData.photoUrl || null,
        lastMessage,
        lastMessageDate,
        unreadCount,
      } as ContactConversation;
    });
    
    const results = await Promise.all(conversationsPromises);
    
    // Filtrar nulls (usuários sem mensagens)
    const conversations = results.filter((conv): conv is ContactConversation => conv !== null);
    
    console.log(`✅ ${conversations.length} conversas encontradas`);
    
    // Ordenar por data da última mensagem (mais recente primeiro)
    return conversations.sort((a, b) => {
      if (!a.lastMessageDate) return 1;
      if (!b.lastMessageDate) return -1;
      return new Date(b.lastMessageDate).getTime() - new Date(a.lastMessageDate).getTime();
    });
  } catch (error: any) {
    console.error('❌ Erro ao buscar conversas:', error);
    console.error('❌ Tipo:', error.code, '- Mensagem:', error.message);
    throw error;
  }
};

/**
 * Buscar mensagens de uma conversa específica
 */
export const fetchMessages = async (userId: string): Promise<Message[]> => {
  try {
    const messagesRef = collection(db, 'contactMessages', userId, 'messages');
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
    const snapshot = await getDocs(messagesQuery);
    
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } as Message));
  } catch (error) {
    console.error('❌ Erro ao buscar mensagens:', error);
    throw error;
  }
};

/**
 * Enviar notificação push para um usuário
 */
const sendPushNotificationToUser = async (userId: string, title: string, body: string): Promise<void> => {
  try {
    const result = await notifyUser({
      userId,
      title,
      body,
      screen: 'message',            // Direciona para a tela de mensagens no Flutter
      customData: { userId },       // ID do usuário (útil para contexto)
    });
    console.log('✅ Notificação enviada:', result);
  } catch (error) {
    console.error('❌ Erro ao enviar notificação push:', error);
    // Não propagar o erro para não bloquear o envio da mensagem
  }
};

/**
 * Enviar mensagem do admin para o cliente
 */
export const sendAdminMessage = async (userId: string, messageText: string): Promise<string> => {
  try {
    const messageId = Date.now().toString();
    const now = getBrazilTimestamp();
    
    const message: Message = {
      id: messageId,
      msg: messageText,
      fromMe: false, // false = admin enviando
      status: 'sent' as MessageStatus,
      createdAt: now,
      updatedAt: now,
    };
    
    const messageRef = doc(db, 'contactMessages', userId, 'messages', messageId);
    await setDoc(messageRef, message);
    
    console.log('✅ Mensagem enviada:', messageId);
    
    // Enviar notificação push para o usuário
    await sendPushNotificationToUser(
      userId,
      '💬 Nova mensagem de suporte',
      messageText.length > 100 ? messageText.substring(0, 100) + '...' : messageText
    );

    return now;
  } catch (error) {
    console.error('❌ Erro ao enviar mensagem:', error);
    throw error;
  }
};

/**
 * Marcar mensagens como lidas
 */
export const markMessagesAsRead = async (userId: string): Promise<void> => {
  try {
    const messagesRef = collection(db, 'contactMessages', userId, 'messages');
    const unreadQuery = query(
      messagesRef,
      where('fromMe', '==', true),
      where('status', '==', 'sent')
    );
    
    const snapshot = await getDocs(unreadQuery);
    
    if (snapshot.empty) return;
    
    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, { status: 'read', updatedAt: getBrazilTimestamp() });
    });
    
    await batch.commit();
    console.log('✅ Mensagens marcadas como lidas');
  } catch (error) {
    console.error('❌ Erro ao marcar mensagens como lidas:', error);
    throw error;
  }
};

/**
 * Resumo das mensagens de suporte não lidas (cliente -> admin) em todo o sistema.
 */
export interface UnreadSupportInfo {
  total: number;
  byUser: Record<string, number>;
  messageIds: string[];
  latest: { userId: string; msg: string; createdAt: string; messageId: string } | null;
}

/**
 * Listener global em tempo real das mensagens de suporte NÃO LIDAS.
 *
 * Usa um collectionGroup em 'messages' (todas as threads de contactMessages de uma
 * vez), filtrando status === 'sent'. As mensagens do cliente (fromMe === true ainda
 * 'sent') são as não lidas pelo admin; as do próprio admin são descartadas aqui.
 *
 * Requer a regra de collectionGroup em firestore.rules e o índice de campo único
 * 'status' com escopo de grupo de coleção (criado automaticamente pelo Firestore).
 */
export const subscribeToUnreadSupport = (
  callback: (info: UnreadSupportInfo) => void
): (() => void) => {
  const unreadQuery = query(collectionGroup(db, 'messages'), where('status', '==', 'sent'));

  const unsubscribe = onSnapshot(
    unreadQuery,
    (snapshot) => {
      const byUser: Record<string, number> = {};
      const messageIds: string[] = [];
      let latest: UnreadSupportInfo['latest'] = null;

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() as any;
        // Apenas mensagens do cliente para o admin contam como "não lidas".
        if (data.fromMe !== true) return;

        // contactMessages/{userId}/messages/{msgId} -> userId é o "avô" do doc.
        const userId = docSnap.ref.parent.parent?.id;
        if (!userId) return;

        byUser[userId] = (byUser[userId] || 0) + 1;
        messageIds.push(docSnap.id);

        const createdAt = data.createdAt || '';
        if (!latest || new Date(createdAt).getTime() > new Date(latest.createdAt).getTime()) {
          latest = { userId, msg: data.msg || '', createdAt, messageId: docSnap.id };
        }
      });

      callback({ total: messageIds.length, byUser, messageIds, latest });
    },
    (error) => {
      console.error('❌ Erro no listener de não-lidas (collectionGroup messages):', error);
    }
  );

  return unsubscribe;
};

/**
 * Retorna o nome do negócio (businessName) de um usuário pelo uid.
 * Usado nas notificações de novas mensagens de suporte.
 */
export const getUserBusinessName = async (userId: string): Promise<string> => {
  try {
    const snap = await getDoc(doc(db, 'user', userId));
    return (snap.exists() && (snap.data() as any).businessName) || 'Cliente';
  } catch {
    return 'Cliente';
  }
};

/**
 * Subscrever para receber atualizações em tempo real das mensagens
 */
export const subscribeToMessages = (
  userId: string,
  callback: (messages: Message[]) => void
): (() => void) => {
  const messagesRef = collection(db, 'contactMessages', userId, 'messages');
  const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
  
  const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
    const messages = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } as Message));
    
    callback(messages);
  });
  
  return unsubscribe;
};

// ==================== BUG REPORTS ====================

/**
 * Buscar todos os bug reports de todos os usuários
 */
export const fetchAllBugReports = async () => {
  try {
    // Primeiro, buscar todos os usuários
    const usersCollection = collection(db, 'user');
    const usersSnapshot = await getDocs(usersCollection);
    
    // 🚀 OTIMIZAÇÃO: Executar todas as consultas em PARALELO usando Promise.all
    const reportsPromises = usersSnapshot.docs.map(async (userDoc) => {
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      try {
        const bugReportsRef = collection(db, `bugReports-${userId}`);
        const bugReportsQuery = query(bugReportsRef, orderBy('createdAt', 'desc'));
        const reportsSnapshot = await getDocs(bugReportsQuery);
        
        return reportsSnapshot.docs.map((reportDoc) => {
          const reportData = reportDoc.data();
          return {
            id: reportDoc.id,
            userId: userId,
            userName: userData.businessName || 'Sem nome',
            userEmail: userData.mail || '',
            userPhotoUrl: userData.photoUrl || null,
            ...reportData,
          };
        });
      } catch (error) {
        // Se a coleção não existir para este usuário, retornar array vazio
        return [];
      }
    });
    
    // Aguardar todas as promises e achatar o array de arrays
    const allReportsArrays = await Promise.all(reportsPromises);
    const allReports = allReportsArrays.flat();
    
    return allReports;
  } catch (error) {
    console.error('❌ Erro ao buscar bug reports:', error);
    throw error;
  }
};

/**
 * Atualizar o status de um bug report
 */
export const updateBugReportStatus = async (
  userId: string,
  reportId: string,
  newStatus: string
): Promise<void> => {
  try {
    const reportRef = doc(db, `bugReports-${userId}`, reportId);
    await updateDoc(reportRef, {
      status: newStatus,
    });
    console.log('✅ Status do bug report atualizado');
  } catch (error) {
    console.error('❌ Erro ao atualizar status:', error);
    throw error;
  }
};